"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, User, Mail, Phone, Building, MessageSquare, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section ref={ref} className={`py-16 px-4 bg-background transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
      <div className="container mx-auto max-w-7xl">
        <div className="mb-6 h-1 w-24 bg-primary rounded-full mx-auto" />
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div className="space-y-8">
            <div className="glass-effect p-8 space-y-6">
              <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Información de Contacto</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="size-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Dirección</h3>
                    <p className="text-muted-foreground">
                      Avenida de Aragón 30 8th. 46021. Valencia. España
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="size-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Teléfono</h3>
                    <p className="text-muted-foreground">
                      +34 614 400 584
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="glass-effect overflow-hidden rounded-lg shadow-soft">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3079.9467283795674!2d-0.3655949!3d39.4863936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd604f4cf0efb06f%3A0x6d1fa64e0c93c4fc!2sAvenida%20de%20Arag%C3%B3n%2C%2030%2C%2046021%20Val%C3%A8ncia%2C%20Valencia%2C%20Spain!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="400" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Ubicación de Vokse Global Consulting" />
            </div>
          </div>

          {}
          <FormProvider className="glass-effect p-8 md:p-10" formId="contact-form-contacto" sectionName="contacto-section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          company: {
            required: false,
            maxLength: 100
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 1000
          }
        }} submitText="Enviar" successMessage="¡Gracias! Nos pondremos en contacto con usted pronto." errorMessage="Error al enviar el formulario. Por favor, inténtelo de nuevo.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-lg text-center">
        <p className="text-foreground text-lg font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        },
        maxLength: {
          value: 50,
          message: "Máximo 50 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nombre</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input placeholder="Su nombre" className="pl-10 shadow-soft hover-lift border-border-strong transition-all duration-[var(--duration-fast)]" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico no válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Correo Electrónico</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="tu@ejemplo.com" className="pl-10 shadow-soft hover-lift border-border-strong transition-all duration-[var(--duration-fast)]" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "El teléfono es obligatorio",
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
          message: "Número de teléfono no válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Teléfono</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="+34 XXX XXX XXX" className="pl-10 shadow-soft hover-lift border-border-strong transition-all duration-[var(--duration-fast)]" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Empresa (opcional)</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input placeholder="Nombre de su empresa" className="pl-10 shadow-soft hover-lift border-border-strong transition-all duration-[var(--duration-fast)]" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "El mensaje es obligatorio",
        minLength: {
          value: 10,
          message: "Mínimo 10 caracteres"
        },
        maxLength: {
          value: 1000,
          message: "Máximo 1000 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Mensaje</FormLabel>
              <FormControl>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 size-4 text-muted-foreground z-10" />
                  <Textarea placeholder="Cuéntenos sobre su proyecto o consulta..." className="pl-10 pt-3 shadow-soft hover-lift border-border-strong transition-all duration-[var(--duration-fast)] min-h-[180px] resize-none" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <div className="mt-8 flex justify-center">
        <Button type="submit" disabled={isSubmitting} className="btn-primary px-8 py-3 text-base shadow-medium min-w-[200px]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Enviando...
            </> : 'Enviar'}
        </Button>
      </div>
    </Form>;
}